import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { practiceTypeLabel } from '@/shared/constants/verticals';
import { usePracticePulse, useResolveWorkItem, useWorkQueue } from '../hooks/usePortfolio';
import { useSupervision } from '../hooks/useSupervision';
import { usePinnedPractices } from '../hooks/usePinnedPractices';
import { usePractice } from '../hooks/usePractices';
import type {
  ConnectionRow,
  LocationAgentHealth,
  PracticePulse,
  WorkItem,
} from '../api/portfolioApi';
import type { SupervisionCard } from '../api/supervisionApi';
import { HealthChip } from '../components/portfolio/HealthChip';
import {
  PILLAR_META,
  PILLAR_ORDER,
  STATE_META,
  formatAge,
  healthTone,
  isAgencyLink,
} from '../components/portfolio/constants';
import { READINESS_META, formatWaiting } from '../components/supervision/constants';
import { PracticeMenu, ViewAsMenu } from '../components/practiceActions/PracticeActions';
import { WorkspacePracticeSwitcher } from '../components/practiceActions/WorkspacePracticeSwitcher';
import { STALE_AFTER_DAYS, isStale } from '../components/practiceActions/stale';
import { ModulesTab } from '../components/modules/ModulesTab';
import { PracticeAgentsTab } from '../components/practiceAgents/PracticeAgentsTab';
import {
  InvitationsCard,
  LocationsCard,
  PracticeInfoCard,
  TeamMembersCard,
} from '../components/settings/PracticeSettingsCards';
import { AccountTypeCard } from '../components/settings/AccountTypeCard';
import { ModelsCard } from '../components/settings/ModelsCard';
import { TextingAdminPanel } from '../components/settings/TextingAdminPanel';
import { TrackedLinksPanel } from '../components/settings/TrackedLinksPanel';
import styles from './PracticeWorkspacePage.module.css';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'modules', label: 'Modules' },
  { key: 'agents', label: 'Agents' },
  { key: 'settings', label: 'Settings' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const SETTINGS_SECTIONS = [
  { key: 'profile', label: 'Profile' },
  { key: 'locations', label: 'Locations' },
  { key: 'team', label: 'Team & invitations' },
  { key: 'connections', label: 'Connections' },
  { key: 'models', label: 'AI models' },
  { key: 'texting', label: 'Texting' },
  // Admin-only short URLs for onboarding; nothing of it shows in the practice's own view.
  { key: 'tracked-links', label: 'Tracked Links' },
] as const;

type SettingsSection = (typeof SETTINGS_SECTIONS)[number]['key'];

function parseTab(value: string | null): TabKey {
  return TABS.some((t) => t.key === value) ? (value as TabKey) : 'overview';
}

function parseSection(value: string | null): SettingsSection {
  return SETTINGS_SECTIONS.some((s) => s.key === value) ? (value as SettingsSection) : 'profile';
}

/**
 * One practice, one location at a time.
 *
 * The header picks the location and every tab follows it. Overview is what
 * needs the agency and what the system did; Modules holds each module's
 * insight, settings, forced runs and resets; Agents holds every per-location
 * agent setting; Settings is the practice record. There is no Admin tab: each
 * of its controls now sits next to the thing it changes.
 */
export function PracticeWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));

  const { data: pulse, isLoading, isError } = usePracticePulse(id);
  const { data: supervision } = useSupervision();
  const { data: pinnedIds } = usePinnedPractices();
  const card = useMemo(
    () => supervision?.cards.find((c) => c.id === id) ?? null,
    [supervision, id],
  );

  const locations = pulse?.locations ?? [];
  const requestedLocation = searchParams.get('location');
  const locationId =
    locations.find((l) => l.id === requestedLocation)?.id ?? locations[0]?.id ?? null;

  // Keep the location in the URL so a refresh, a shared link and a tab change
  // all land on the same one.
  useEffect(() => {
    if (locationId && requestedLocation !== locationId) {
      const params = new URLSearchParams(searchParams);
      params.set('location', locationId);
      setSearchParams(params, { replace: true });
    }
  }, [locationId, requestedLocation, searchParams, setSearchParams]);

  const setParam = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (value === null) params.delete(key);
    else params.set(key, value);
    setSearchParams(params, { replace: true });
  };

  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    // Tab-local params do not survive a tab change.
    params.delete('section');
    params.delete('module');
    setSearchParams(params, { replace: true });
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !pulse || !id) {
    return (
      <div className={styles.notFound}>
        <p>Practice not found.</p>
        <Button onClick={() => navigate('/agency')}>Back to Practices</Button>
      </div>
    );
  }

  const { row } = pulse;
  const state = STATE_META[row.state];
  const snoozed = !!row.snoozedAt;

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate('/agency')}>
        ← Practices
      </button>

      <header className={styles.header}>
        <div className={styles.identity}>
          <div className={styles.titleRow}>
            <WorkspacePracticeSwitcher
              practiceId={row.id}
              practiceName={row.name}
              tab={tab === 'overview' ? undefined : tab}
            />
            {snoozed ? (
              <span className={clsx(styles.statePill, styles.muted)}>Snoozed</span>
            ) : (
              <span className={clsx(styles.statePill, styles[state.tone])}>{state.label}</span>
            )}
          </div>
          <p className={styles.subtitle}>
            {practiceTypeLabel(row.vertical, row.specialty)}
            {` · ${row.locationsCount} location${row.locationsCount === 1 ? '' : 's'}`}
            {card?.lastSystemActionAt
              ? ` · last system action ${formatAge(card.lastSystemActionAt)} ago`
              : ''}
            {card
              ? ` · client last login ${card.clientLastLoginAt ? `${formatAge(card.clientLastLoginAt)} ago` : 'never'}`
              : ''}
          </p>
        </div>
        <div className={styles.headerRight}>
          {locations.length > 0 && (
            <select
              className={styles.locationSelect}
              aria-label="Location"
              value={locationId ?? ''}
              disabled={locations.length === 1}
              onChange={(e) => setParam('location', e.target.value)}
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.city ? `${l.name} — ${l.city}` : l.name}
                </option>
              ))}
            </select>
          )}
          <HealthChip score={row.healthScore} previousScore={row.previousHealthScore} size="lg" />
          <ViewAsMenu practiceId={row.id} practiceName={row.name} />
          <PracticeMenu
            practiceId={row.id}
            practiceName={row.name}
            snoozed={snoozed}
            story={card?.story ?? []}
            pinned={(pinnedIds ?? []).includes(row.id)}
            pinnedCount={pinnedIds?.length ?? 0}
          />
        </div>
      </header>

      <nav className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={clsx(styles.tab, tab === t.key && styles.tabActive)}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'overview' && row.openForUs > 0 && (
              <span className={styles.tabBadge}>{row.openForUs}</span>
            )}
          </button>
        ))}
      </nav>

      {!locationId ? (
        <div className={styles.emptyPanel}>
          This practice has no locations yet. Add one under Settings › Locations.
        </div>
      ) : (
        <>
          {tab === 'overview' && (
            <OverviewTab
              practiceId={id}
              locationId={locationId}
              pulse={pulse}
              card={card}
              onOpenAgents={() => setTab('agents')}
            />
          )}
          {tab === 'modules' && (
            <ModulesTab
              practiceId={id}
              locationId={locationId}
              modulesInUse={card?.modules ?? []}
            />
          )}
          {tab === 'agents' && <PracticeAgentsTab practiceId={id} locationId={locationId} />}
          {tab === 'settings' && (
            <SettingsTab
              practiceId={id}
              locationId={locationId}
              pulse={pulse}
              card={card}
              section={parseSection(searchParams.get('section'))}
              onSection={(section) => setParam('section', section === 'profile' ? null : section)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────

/**
 * What needs the agency, what is waiting on the client, and what the system
 * did — for the location in the header. An item with no location is
 * practice-wide and shows for every location.
 */
function OverviewTab({
  practiceId,
  locationId,
  pulse,
  card,
  onOpenAgents,
}: {
  practiceId: string;
  locationId: string;
  pulse: PracticePulse;
  card: SupervisionCard | null;
  onOpenAgents: () => void;
}) {
  const { row, sla } = pulse;
  const agentHealth = pulse.agentHealth.find((h) => h.locationId === locationId);
  const { data: items, isLoading } = useWorkQueue({ practiceId });
  const [staleOpen, setStaleOpen] = useState(false);

  const forLocation = useMemo(
    () => (items ?? []).filter((i) => i.locationId === null || i.locationId === locationId),
    [items, locationId],
  );
  const needsYou = forLocation.filter((i) => i.ballWith === 'us' && !isStale(i.createdAt));
  const stale = forLocation.filter((i) => i.ballWith === 'us' && isStale(i.createdAt));
  const waiting = forLocation.filter((i) => i.ballWith === 'client');
  // Only things that ran. Items waiting on someone appear once, above, never again here.
  const ran = (card?.log ?? []).filter((entry) => entry.awaiting === null);

  return (
    <div className={styles.stack}>
      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>
            Health <span className={styles.panelScope}>practice-wide</span>
          </h2>
          <p className={styles.panelSubtitle}>
            Pillars that do not apply are excluded from the score rather than counted as zero.
          </p>
        </div>
        {row.healthScore === null ? (
          <p className={styles.emptyNote}>
            Nothing measurable has happened for this practice yet, so there is nothing to score.
          </p>
        ) : (
          <div className={styles.pillarGrid}>
            {PILLAR_ORDER.map((key) => {
              const value = row.pillars[key];
              const meta = PILLAR_META[key];
              const tone = value === null ? 'muted' : healthTone(value);
              return (
                <div key={key} className={styles.pillarCard}>
                  <span className={styles.pillarLabel}>{meta.label}</span>
                  <span className={clsx(styles.pillarValue, styles[tone])}>
                    {value === null ? 'n/a' : value}
                  </span>
                  <span className={styles.pillarTrack}>
                    <span
                      className={clsx(styles.pillarFill, styles[tone])}
                      style={{ width: `${value ?? 0}%` }}
                    />
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {agentHealth && agentHealth.hired > 0 && (
          <AgentHealthRow health={agentHealth} onOpen={onOpenAgents} />
        )}
      </section>

      <section className={styles.panel}>
        <div className={clsx(styles.panelHeader, styles.panelHeaderRow)}>
          <h2 className={styles.panelTitle}>Needs you · {needsYou.length}</h2>
          <span className={styles.slaLine}>
            Last 30 days: {sla.metOnTimePct === null ? '—' : `${sla.metOnTimePct}% on time`} ·
            median {sla.medianResponseHours === null ? '—' : `${sla.medianResponseHours}h`} ·{' '}
            {sla.breached} breached
          </span>
        </div>
        {isLoading && <Skeleton height={64} />}
        {!isLoading && needsYou.length === 0 && (
          <p className={styles.emptyNote}>Nothing needs you at this location.</p>
        )}
        {needsYou.map((item) => (
          <WorkRow key={item.id} item={item} />
        ))}
        {stale.length > 0 && (
          <details
            className={styles.fold}
            open={staleOpen}
            onToggle={(e) => setStaleOpen((e.target as HTMLDetailsElement).open)}
          >
            <summary className={styles.foldSummary}>
              Older than {STALE_AFTER_DAYS} days · {stale.length}
              <span className={styles.foldHint}>not counted above</span>
            </summary>
            {stale.map((item) => (
              <WorkRow key={item.id} item={item} />
            ))}
            <DismissAll items={stale} />
          </details>
        )}
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Waiting on them · {waiting.length}</h2>
        </div>
        {!isLoading && waiting.length === 0 && (
          <p className={styles.emptyNote}>Nothing is waiting on the practice.</p>
        )}
        {waiting.map((item) => (
          <WorkRow key={item.id} item={item} />
        ))}
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>
            What the system did <span className={styles.panelScope}>practice-wide</span>
          </h2>
          <p className={styles.panelSubtitle}>
            Only things that ran. Items waiting on you appear once, above.
          </p>
        </div>
        {ran.length === 0 && <p className={styles.emptyNote}>No recorded activity yet.</p>}
        {ran.map((entry) => (
          <div key={entry.id} className={styles.logRow}>
            <span className={styles.logAge}>{formatWaiting(entry.at)}</span>
            <span
              className={clsx(styles.logActor, entry.actor !== 'System' && styles.logActorAgent)}
            >
              {entry.actor}
            </span>
            <span className={styles.logMessage}>{entry.message}</span>
          </div>
        ))}
      </section>

      <section className={styles.panel}>
        <div className={clsx(styles.panelHeader, styles.panelHeaderRow)}>
          <h2 className={styles.panelTitle}>This week&apos;s story</h2>
          <CopyStoryButton name={row.name} story={card?.story ?? []} />
        </div>
        <p className={styles.panelSubtitle}>
          They rarely sign in — this is what goes in the call or the email.
        </p>
        <ul className={styles.storyList}>
          {(card?.story ?? []).map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
        {(card?.story ?? []).length === 0 && (
          <p className={styles.emptyNote}>Nothing to tell yet this week.</p>
        )}
      </section>
    </div>
  );
}

/**
 * One line for every hired agent at this location: the share of their runs that succeeded in the
 * last 24 hours. Per-agent counts are in the tooltip; the Agents tab has the rest.
 */
function AgentHealthRow({ health, onOpen }: { health: LocationAgentHealth; onOpen: () => void }) {
  const breakdown = health.agents
    .map((a) => `${a.personaName}: ${a.failed} failed of ${a.runs}`)
    .join(' · ');
  return (
    <div className={clsx(styles.workRow, styles.agentHealthRow)} title={breakdown || undefined}>
      <div className={styles.workBody}>
        <span className={styles.workTitle}>Agents · this location</span>
        <span className={styles.workMeta}>
          {health.score === null
            ? 'No agent runs in the last 24 hours'
            : `${health.failed} failed of ${health.runs} run${health.runs === 1 ? '' : 's'} in the last 24 hours`}
        </span>
      </div>
      <div className={styles.workActions}>
        <HealthChip score={health.score} />
        <Button size="sm" variant="secondary" onClick={onOpen}>
          Open agents
        </Button>
      </div>
    </div>
  );
}

function WorkRow({ item }: { item: WorkItem }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { startImpersonation } = useImpersonation();
  const resolve = useResolveWorkItem();

  const act = () => {
    if (!item.deepLink) return;
    if (!isAgencyLink(item.deepLink))
      startImpersonation({ id: item.practiceId, name: item.practiceName });
    navigate(item.deepLink);
  };

  const dismiss = async () => {
    try {
      await resolve.mutateAsync({ id: item.id, update: { status: 'dismissed' } });
      toast.success('Dismissed');
    } catch {
      toast.error('Could not dismiss this item');
    }
  };

  const nudge = async () => {
    try {
      await resolve.mutateAsync({ id: item.id, update: { status: 'done' } });
      toast.success('Marked done');
    } catch {
      toast.error('Could not update this item');
    }
  };

  return (
    <div className={styles.workRow}>
      <div className={styles.workBody}>
        <span className={styles.workTitle}>{item.title}</span>
        <span className={styles.workMeta}>
          {item.description}
          {' · waiting '}
          {formatWaiting(item.createdAt)}
        </span>
      </div>
      <div className={styles.workActions}>
        {item.ballWith === 'us' && item.actionLabel && item.deepLink && (
          <Button size="sm" onClick={act}>
            {item.actionLabel}
          </Button>
        )}
        {item.ballWith === 'client' ? (
          <Button variant="ghost" size="sm" onClick={nudge} disabled={resolve.isPending}>
            Mark done
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={dismiss} disabled={resolve.isPending}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
}

function DismissAll({ items }: { items: WorkItem[] }) {
  const toast = useToast();
  const resolve = useResolveWorkItem();
  const [busy, setBusy] = useState(false);

  const dismissAll = async () => {
    setBusy(true);
    try {
      await Promise.all(
        items.map((item) => resolve.mutateAsync({ id: item.id, update: { status: 'dismissed' } })),
      );
      toast.success(`${items.length} stale item${items.length === 1 ? '' : 's'} dismissed`);
    } catch {
      toast.error('Could not dismiss every item — some may remain');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.foldActions}>
      <Button variant="secondary" size="sm" onClick={dismissAll} disabled={busy}>
        Dismiss all {items.length}
      </Button>
    </div>
  );
}

function CopyStoryButton({ name, story }: { name: string; story: string[] }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = `${name}\n\n${story.map((l) => `• ${l}`).join('\n')}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — your browser blocked clipboard access');
    }
  };

  return (
    <Button variant="secondary" size="sm" onClick={copy} disabled={story.length === 0}>
      {copied ? 'Copied' : 'Copy for email'}
    </Button>
  );
}

// ─── Settings ─────────────────────────────────────────────────────────────────

/** The practice record: profile, locations, team, connections and models. */
function SettingsTab({
  practiceId,
  locationId,
  pulse,
  card,
  section,
  onSection,
}: {
  practiceId: string;
  locationId: string;
  pulse: PracticePulse;
  card: SupervisionCard | null;
  section: SettingsSection;
  onSection: (section: SettingsSection) => void;
}) {
  const { data: practice, isLoading } = usePractice(practiceId);
  const location = practice?.locations.find((l) => l.id === locationId) ?? null;

  return (
    <div className={styles.sideNavLayout}>
      <nav className={styles.sideNav} aria-label="Settings">
        {SETTINGS_SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={clsx(styles.sideNavItem, section === s.key && styles.sideNavItemActive)}
            aria-current={section === s.key ? 'true' : undefined}
            onClick={() => onSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </nav>

      <div className={styles.stack}>
        {isLoading && <Skeleton height={240} borderRadius="var(--card-radius)" />}
        {!isLoading && !practice && <div className={styles.emptyPanel}>Practice not found.</div>}
        {practice && section === 'profile' && (
          <>
            <PracticeInfoCard practice={practice} />
            <AccountTypeCard practiceId={practiceId} featureFlags={practice.featureFlags ?? {}} />
          </>
        )}
        {practice && section === 'locations' && (
          <>
            <LocationsCard practice={practice} />
            {card && (
              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <h2 className={styles.panelTitle}>
                    Setup steps <span className={styles.panelScope}>practice-wide</span>
                  </h2>
                  <p className={styles.panelSubtitle}>
                    Only what their modules need. A step marked “not in scope” is a module this
                    practice does not run.
                  </p>
                </div>
                {card.readiness.map((step) => {
                  const meta = READINESS_META[step.state];
                  return (
                    <div
                      key={step.key}
                      className={clsx(styles.readyRow, step.state === 'na' && styles.readyRowNa)}
                    >
                      <span className={clsx(styles.readyGlyph, styles[meta.tone])}>
                        {meta.glyph}
                      </span>
                      <span className={styles.readyLabel}>
                        {step.label}
                        {step.note && (
                          <span
                            className={clsx(
                              styles.readyNote,
                              step.state === 'blocked' && styles.error,
                            )}
                          >
                            {' — '}
                            {step.note}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </section>
            )}
          </>
        )}
        {practice && section === 'team' && (
          <>
            <TeamMembersCard practice={practice} />
            <InvitationsCard practice={practice} />
          </>
        )}
        {practice && section === 'connections' && (
          <ConnectionsPanel pulse={pulse} practiceId={practiceId} practiceName={practice.name} />
        )}
        {practice && section === 'models' && location && (
          <ModelsCard practiceId={practiceId} location={location} />
        )}
        {practice && section === 'texting' && location && (
          <TextingAdminPanel
            practiceId={practiceId}
            practiceName={practice.name}
            location={location}
          />
        )}
        {practice && section === 'tracked-links' && <TrackedLinksPanel practiceId={practiceId} />}
      </div>
    </div>
  );
}

const CONNECTION_COPY: Record<ConnectionRow['state'], { label: string; tone: string }> = {
  connected: { label: 'syncing', tone: 'success' },
  stale: { label: 'stale', tone: 'warning' },
  expired: { label: 'needs reconnect', tone: 'error' },
  missing: { label: 'not connected', tone: 'muted' },
};

/**
 * Data connections, and the one way to fix them: connecting is done inside the
 * practice's own settings (Paid, or Organic & Offline), in a recorded View-as session.
 */
function ConnectionsPanel({
  pulse,
  practiceId,
  practiceName,
}: {
  pulse: PracticePulse;
  practiceId: string;
  practiceName: string;
}) {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();

  // The ad accounts are in Settings > Paid; Search Console and Business Profile in Organic & Offline.
  const firstGap = pulse.connections.find((c) => c.state !== 'connected');
  const connect = () => {
    startImpersonation({ id: practiceId, name: practiceName });
    navigate(firstGap && /ads/.test(firstGap.provider) ? '/settings/paid' : '/settings/organic');
  };

  return (
    <section className={styles.panel}>
      <div className={clsx(styles.panelHeader, styles.panelHeaderRow)}>
        <h2 className={styles.panelTitle}>
          Data connections <span className={styles.panelScope}>practice-wide</span>
        </h2>
        <Button variant="secondary" size="sm" onClick={connect}>
          Connect as practice
        </Button>
      </div>
      <p className={styles.panelSubtitle}>
        Connecting opens their settings (Paid for ad accounts, Organic &amp; Offline for search) in
        a recorded View-as-practice session.
      </p>
      {pulse.connections.map((connection) => {
        const copy = CONNECTION_COPY[connection.state];
        return (
          <div key={connection.provider} className={styles.connectionRow}>
            <span className={clsx(styles.connectionDot, styles[copy.tone])} aria-hidden />
            <span className={styles.connectionName}>{connection.label}</span>
            <span className={clsx(styles.connectionState, styles[copy.tone])}>
              {connection.state === 'connected' && connection.lastSyncAt
                ? `syncing · ${formatAge(connection.lastSyncAt)} ago`
                : copy.label}
            </span>
          </div>
        );
      })}
    </section>
  );
}
